import React, { useCallback, useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { TextSelection } from '@tiptap/pm/state';
import { Fragment, Node as ProseMirrorNode } from '@tiptap/pm/model';
import { common, createLowlight } from 'lowlight';

const lowlight = createLowlight(common);

type HeadingLevel = 1 | 2 | 3 | 4;

/** Trim leading/trailing hardBreak nodes from an inline fragment */
const trimHardBreaks = (content: Fragment): ProseMirrorNode[] => {
  const nodes: ProseMirrorNode[] = [];
  content.forEach((node) => nodes.push(node));
  while (nodes.length && nodes[0].type.name === 'hardBreak') nodes.shift();
  while (nodes.length && nodes[nodes.length - 1].type.name === 'hardBreak') nodes.pop();
  return nodes;
};

/**
 * Apply heading only to the exact selected text.
 * Soft-breaks (<br>) keep multiple visual lines in one paragraph — we rewrite
 * that parent into: [paragraph before?] + [heading] + [paragraph after?].
 */
const applyHeadingToSelection = (editor: Editor, level: HeadingLevel) => {
  const { from, to, empty, $from, $to } = editor.state.selection;

  if (empty) {
    editor.chain().focus().toggleHeading({ level }).run();
    return;
  }

  const sameParent = $from.sameParent($to) && $from.parent.isTextblock;
  const isPartial =
    sameParent && ($from.parentOffset > 0 || $to.parentOffset < $from.parent.content.size);

  // Whole block(s) already — normal TipTap toggle
  if (!isPartial) {
    editor.chain().focus().toggleHeading({ level }).run();
    return;
  }

  editor
    .chain()
    .focus()
    .command(({ tr, state, dispatch }) => {
      if (!dispatch) return true;

      const paragraphType = state.schema.nodes.paragraph;
      const headingType = state.schema.nodes.heading;
      if (!paragraphType || !headingType) return false;

      const parentDepth = $from.depth;
      const parentPos = $from.before(parentDepth);
      const parentNode = $from.parent;
      const parentStart = $from.start(parentDepth);
      const parentEnd = $from.end(parentDepth);

      const beforeRaw = state.doc.slice(parentStart, from).content;
      const selectedRaw = state.doc.slice(from, to).content;
      const afterRaw = state.doc.slice(to, parentEnd).content;

      const beforeNodes = trimHardBreaks(beforeRaw);
      const selectedNodes = trimHardBreaks(selectedRaw);
      const afterNodes = trimHardBreaks(afterRaw);

      if (!selectedNodes.length) return false;

      const replacement: ProseMirrorNode[] = [];
      if (beforeNodes.length) {
        replacement.push(paragraphType.create(null, beforeNodes));
      }
      replacement.push(headingType.create({ level }, selectedNodes));
      if (afterNodes.length) {
        replacement.push(paragraphType.create(null, afterNodes));
      }

      tr.replaceWith(parentPos, parentPos + parentNode.nodeSize, replacement);

      // Select the new heading contents
      const headingIndex = beforeNodes.length ? 1 : 0;
      let pos = parentPos + 1;
      for (let i = 0; i < headingIndex; i++) {
        pos += replacement[i].nodeSize;
      }
      const headingStart = pos + 1;
      const headingEnd = pos + replacement[headingIndex].nodeSize - 1;
      tr.setSelection(TextSelection.create(tr.doc, headingStart, headingEnd));

      dispatch(tr);
      return true;
    })
    .run();
};

interface TipTapEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const MenuButton: React.FC<{
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
  color?: string;
}> = ({ onClick, isActive, disabled, title, children, color }) => (
  <button
    type="button"
    // Keep editor selection when clicking toolbar (avoid blur collapsing selection)
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded transition-colors flex items-center justify-center text-xs
      ${isActive 
        ? (color || 'bg-dusk-copper text-white') 
        : 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

const TipTapEditor: React.FC<TipTapEditorProps> = ({
  value,
  onChange,
  placeholder = "Start writing your article...",
  className = ""
}) => {
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
        heading: { levels: [1, 2, 3, 4] },
      }),
      Underline,
      Highlight.configure({ multicolor: true }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'rounded-lg max-w-full mx-auto',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-400 underline hover:text-blue-300',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({
        types: ['heading', 'paragraph', 'image'],
      }),
      CodeBlockLowlight.configure({ lowlight }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[300px] px-4 py-3 text-gray-200',
      },
    },
  });

  // Sync external value changes
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Only update if external value is substantially different
      const currentHTML = editor.getHTML();
      if (value !== currentHTML && Math.abs((value?.length || 0) - currentHTML.length) > 10) {
        editor.commands.setContent(value || '', { emitUpdate: false });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleImageUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editor) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;

      const response = await fetch(`${apiUrl}/api/upload/blog-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      if (result.success) {
        const directUrl = `${apiUrl}${result.url}`;
        editor.chain().focus().setImage({ src: directUrl }).run();
      } else {
        alert(`Upload failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  }, [editor]);

  const addLink = useCallback(() => {
    if (!editor) return;
    if (linkUrl) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    setShowLinkInput(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  if (!editor) return null;

  return (
    <div className={`tiptap-editor ${className}`}>
      {/* Sticky toolbar — stays visible while scrolling long articles */}
      <div className="tiptap-toolbar sticky top-0 z-30 flex flex-wrap gap-1 p-2 bg-gray-800 border border-gray-600 rounded-t-lg shadow-md">
        {/* Text style */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
        >
          <strong>B</strong>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
        >
          <em>I</em>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Underline (Ctrl+U)"
        >
          <span className="underline">U</span>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          title="Strikethrough"
        >
          <span className="line-through">S</span>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          isActive={editor.isActive('highlight')}
          title="Highlight"
        >
          <span className="bg-yellow-400/60 text-black px-1 rounded">H</span>
        </MenuButton>

        <div className="w-px bg-gray-600 mx-0.5 self-stretch"></div>

        {/* Headings — only the selected block/range, not surrounding text */}
        <MenuButton
          onClick={() => applyHeadingToSelection(editor, 1)}
          isActive={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          H1
        </MenuButton>
        <MenuButton
          onClick={() => applyHeadingToSelection(editor, 2)}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          H2
        </MenuButton>
        <MenuButton
          onClick={() => applyHeadingToSelection(editor, 3)}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          H3
        </MenuButton>

        <div className="w-px bg-gray-600 mx-0.5"></div>

        {/* Lists */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></svg>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>
        </MenuButton>

        <div className="w-px bg-gray-600 mx-0.5"></div>

        {/* Block elements */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Quote"
        >
          <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={editor.isActive('code')}
          title="Inline Code"
        >
          <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          isActive={editor.isActive('codeBlock')}
          title="Code Block"
        >
          {'</>'}
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal Rule"
        >
          ―
        </MenuButton>

        <div className="w-px bg-gray-600 mx-0.5"></div>

        {/* Alignment */}
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Align Left"
          color="bg-green-700 text-white"
        >
          <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8M4 18h16" /></svg>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Align Center"
          color="bg-green-700 text-white"
        >
          <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M8 12h8M4 18h16" /></svg>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Align Right"
          color="bg-green-700 text-white"
        >
          <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M12 12h8M4 18h16" /></svg>
        </MenuButton>

        <div className="w-px bg-gray-600 mx-0.5"></div>

        {/* Link */}
        <MenuButton
          onClick={() => {
            if (editor.isActive('link')) {
              editor.chain().focus().unsetLink().run();
            } else {
              const previousUrl = editor.getAttributes('link').href;
              setLinkUrl(previousUrl || '');
              setShowLinkInput(true);
            }
          }}
          isActive={editor.isActive('link')}
          title="Link"
        >
          <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
        </MenuButton>

        {/* Image upload */}
        <div className="relative">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            id="tiptap-image-upload"
            disabled={uploadingImage}
          />
          <label
            htmlFor="tiptap-image-upload"
            className={`flex items-center p-1.5 bg-dusk-copper hover:bg-dusk-amber text-white rounded transition-colors cursor-pointer text-xs ${
              uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="Upload Image"
          >
            <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </label>
        </div>

        {uploadingImage && (
          <div className="flex items-center gap-1 px-2 py-1 text-xs text-dusk-amber">
            <div className="animate-spin w-3 h-3 border border-dusk-amber border-t-transparent rounded-full"></div>
            Uploading...
          </div>
        )}

        <div className="w-px bg-gray-600 mx-0.5"></div>

        {/* Undo/Redo */}
        <MenuButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl+Z)"
        >
          <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4" /></svg>
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl+Shift+Z)"
        >
          <svg className="w-4 h-4" stroke="currentColor" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a5 5 0 00-5 5v2M21 10l-4-4M21 10l-4 4" /></svg>
        </MenuButton>
      </div>

      {/* Link input popup */}
      {showLinkInput && (
        <div className="flex items-center gap-2 p-2 bg-gray-750 border-x border-gray-600" style={{ backgroundColor: '#2a2d37' }}>
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
            className="flex-1 px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-dusk-copper"
            onKeyDown={(e) => { if (e.key === 'Enter') addLink(); if (e.key === 'Escape') setShowLinkInput(false); }}
            autoFocus
          />
          <button type="button" onClick={addLink} className="px-3 py-1.5 bg-dusk-copper hover:bg-dusk-copper-deep text-white text-sm rounded">Apply</button>
          <button type="button" onClick={() => setShowLinkInput(false)} className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded">Cancel</button>
        </div>
      )}

      {/* Editor content */}
      <div className="bg-gray-700 border border-gray-600 border-t-0 rounded-b-lg overflow-hidden">
        <EditorContent editor={editor} />
      </div>

      {/* Help text */}
      <div className="mt-2 text-xs text-gray-400">
        <div className="flex flex-wrap gap-4">
          <span>Ctrl+B <strong>bold</strong></span>
          <span>Ctrl+I <em>italic</em></span>
          <span>Ctrl+U <span className="underline">underline</span></span>
          <span>Markdown shortcuts: # heading, **, *, -, &gt;, ```</span>
          <span className="text-green-400">Alignment buttons</span>
          <span className="text-dusk-amber">Image upload</span>
        </div>
      </div>

      {/* Editor styles */}
      <style>{`
        .tiptap-editor .tiptap-toolbar {
          background-color: #1f2937;
          backdrop-filter: blur(8px);
        }
        .tiptap-editor .tiptap {
          min-height: 300px;
          outline: none;
        }
        .tiptap-editor .tiptap p {
          margin: 0.5em 0;
          color: #e5e7eb;
        }
        .tiptap-editor .tiptap h1 {
          font-size: 2em;
          font-weight: 700;
          margin: 0.67em 0;
          color: #f9fafb;
        }
        .tiptap-editor .tiptap h2 {
          font-size: 1.5em;
          font-weight: 600;
          margin: 0.75em 0;
          color: #f3f4f6;
        }
        .tiptap-editor .tiptap h3 {
          font-size: 1.25em;
          font-weight: 600;
          margin: 0.75em 0;
          color: #e5e7eb;
        }
        .tiptap-editor .tiptap h4 {
          font-size: 1.1em;
          font-weight: 600;
          margin: 0.75em 0;
          color: #d1d5db;
        }
        .tiptap-editor .tiptap ul {
          list-style: disc;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .tiptap-editor .tiptap ol {
          list-style: decimal;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .tiptap-editor .tiptap li {
          margin: 0.25em 0;
          color: #e5e7eb;
        }
        .tiptap-editor .tiptap blockquote {
          border-left: 3px solid #4b5563;
          padding-left: 1em;
          margin: 0.5em 0;
          color: #9ca3af;
          font-style: italic;
        }
        .tiptap-editor .tiptap code {
          background: #374151;
          color: #60a5fa;
          padding: 0.15em 0.4em;
          border-radius: 4px;
          font-family: 'Fira Code', 'Courier New', monospace;
          font-size: 0.9em;
        }
        .tiptap-editor .tiptap pre {
          background: #1f2937;
          color: #e5e7eb;
          border: 1px solid #374151;
          border-radius: 8px;
          padding: 1em;
          margin: 0.75em 0;
          overflow-x: auto;
          font-family: 'Fira Code', 'Courier New', monospace;
          font-size: 0.9em;
        }
        .tiptap-editor .tiptap pre code {
          background: none;
          color: inherit;
          padding: 0;
          border-radius: 0;
          font-size: inherit;
        }
        .tiptap-editor .tiptap img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1em auto;
          display: block;
        }
        .tiptap-editor .tiptap a {
          color: #60a5fa;
          text-decoration: underline;
        }
        .tiptap-editor .tiptap a:hover {
          color: #93bbfd;
        }
        .tiptap-editor .tiptap hr {
          border: none;
          border-top: 1px solid #4b5563;
          margin: 1.5em 0;
        }
        .tiptap-editor .tiptap mark {
          background-color: rgba(250, 204, 21, 0.4);
          border-radius: 2px;
          padding: 0 2px;
        }
        .tiptap-editor .tiptap p.is-editor-empty:first-child::before {
          color: #6b7280;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .tiptap-editor .tiptap .hljs-keyword,
        .tiptap-editor .tiptap .hljs-selector-tag,
        .tiptap-editor .tiptap .hljs-title,
        .tiptap-editor .tiptap .hljs-section,
        .tiptap-editor .tiptap .hljs-doctag,
        .tiptap-editor .tiptap .hljs-name,
        .tiptap-editor .tiptap .hljs-strong {
          color: #c678dd;
        }
        .tiptap-editor .tiptap .hljs-string,
        .tiptap-editor .tiptap .hljs-attr,
        .tiptap-editor .tiptap .hljs-symbol,
        .tiptap-editor .tiptap .hljs-bullet,
        .tiptap-editor .tiptap .hljs-addition {
          color: #98c379;
        }
        .tiptap-editor .tiptap .hljs-comment,
        .tiptap-editor .tiptap .hljs-quote,
        .tiptap-editor .tiptap .hljs-deletion,
        .tiptap-editor .tiptap .hljs-meta {
          color: #5c6370;
        }
        .tiptap-editor .tiptap .hljs-number,
        .tiptap-editor .tiptap .hljs-literal {
          color: #d19a66;
        }
      `}</style>
    </div>
  );
};

export default TipTapEditor;
